import React, { useContext } from 'react'
import { useIntl } from 'react-intl'

import SavedItemRemoveButton from '../ItemCard/Common/SavedItemRemoveButton'
import FlightItemCardHeading from '../ItemCard/Flight/FlightItemCardHeading'
import type { ItemCardVariant } from '../ItemCard/types'

import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import {
  FlightsFareTypes,
} from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import {
  formatOccupantsShort,
  sumUpOccupancies,
} from 'lib/offer/occupancyUtils'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import AttachedDocument from 'tripPlanner/components/Documents/AttachedDocument'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import { useLowestFlightPrice } from 'tripPlanner/hooks/api/flights'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import type { LEFlightItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange } from 'tripPlanner/utils'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'

export interface Props {
  item: LEFlightItem
  tripNameLabel?: React.ReactNode
  variant: ItemCardVariant
  onFlightSearchClick: () => void
}

export default function FlightSearchTileContent({
  item,
  tripNameLabel,
  variant,
  onFlightSearchClick,
}: Props) {
  const {
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      currencyCode,
      regionCode,
      adultPrice,
      fareClass,
      fareType,
      totalPrice,
      travellerCounts,
    },
  } = item

  const canEdit = useCanEditTrip()
  const intl = useIntl()
  const { fareTypeLabelMap, fareCabinLabelMap } = useFlightFareLabels()
  const effectiveCurrencyCode = currencyCode
  const { data: price, isLoading: priceLoading } = useLowestFlightPrice({
    originAirportCode,
    destinationAirportCode,
    currencyCode: effectiveCurrencyCode,
    regionCode,
    shouldQuery:
      !adultPrice && fareType === FlightsFareTypes.RETURN,
  })

  const minAdultPrice = adultPrice ?? price
  const showPrice = !priceLoading && !!minAdultPrice

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }
  const showTotal = totalPrice && sumUpOccupancies([travellerCount]) > 1
  const fareTypeLabel = fareTypeLabelMap[fareType]
  const fareClassLabel = fareCabinLabelMap[fareClass as keyof typeof fareCabinLabelMap]

  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const description = [
    formatShortDateRange(item),
    fareClassLabel,
    fareTypeLabel,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(` ${sep} `)

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  const source = useTripClientSource()
  const flightSearchUrl = addQueryParamsToPath(
    buildStandaloneFlightSearchURL(item.savedItemData),
    {
      source,
    },
  )

  const fareTypeFromLabel = intl.formatMessage(
    {
      id: '/ySJPm0Fzf',
      defaultMessage: '{fareType} from',
      description: 'Label showing fare type followed by "from" before the flight price',
    },
    {
      fareType: fareTypeLabel,
    },
  )

  const priceWithAsterisk = intl.formatMessage(
    {
      id: 'F+pC64DD6f',
      defaultMessage: '{price}*',
      description: 'Flight price with asterisk referencing the price disclaimer',
    },
    {
      price: (
        <FormatCurrency
          value={minAdultPrice ?? 0}
          currency={effectiveCurrencyCode}
          format="dynamicDollar"
        />
      ),
    },
  )

  const totalFromLabel = totalPrice ? intl.formatMessage(
    {
      id: '3Mk0Q2o0xT',
      defaultMessage: 'Total from {price}',
      description: 'Total flight price label showing the total cost',
    },
    {
      price: <FormatCurrency value={totalPrice} />,
    },
  ) : undefined

  return (
    <>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="space-between"
      >
        <BodyTextBlock variant="small" startIcon={<LinePlaneFlyIcon />}>
          {intl.formatMessage({
            id: 'CrKRKryCig',
            defaultMessage: 'Flight',
            description: 'Label for a flight search tile in the trip planner',
          })}
        </BodyTextBlock>
        {canEdit && <SavedItemRemoveButton item={item} />}
      </Group>
      <VerticalSpacer gap={4}>
        <FlightItemCardHeading item={item} />
        <BodyText variant="medium">{description}</BodyText>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        {tripNameLabel}

        {!item.isSuggested && <>
          {attachedDocuments.map((document) => (
            <AttachedDocument key={document.filename} document={document} item={item} />
          ))}
          {documentContext && canEdit && (
            <AssistChip
              size="small"
              onClick={() => documentContext.onRequestAddDocument(item.id)}
              variant="filled"
              startIcon={<LinePaperclipIcon />}
            >
              {intl.formatMessage({
                id: 'G7RAyegSlH',
                defaultMessage: 'Attach document',
                description: 'Button to attach a document to a saved flight search',
              })}
            </AssistChip>
          )}
        </>}

        <Group
          direction="horizontal"
          horizontalAlign={showPrice ? 'space-between' : 'end'}
          verticalAlign="end"
        >
          {showPrice && (
            <div>
              <Caption variant="medium">{fareTypeFromLabel}</Caption>
              <div>
                <Heading as="span" variant="heading6">
                  {priceWithAsterisk}
                </Heading>
                <BodyText as="span" variant="small">
                  {' '}
                  /adult
                </BodyText>
                {showTotal && (
                  <Caption variant="medium" colour="neutral-three">
                    {totalFromLabel}
                  </Caption>
                )}
              </div>
            </div>
          )}
          {canEdit && (
            <TextButton
              kind={variant === 'drawer' ? 'primary' : 'secondary'}
              to={flightSearchUrl}
              onClick={onFlightSearchClick}
            >
              {intl.formatMessage({
                id: 'DCP4AqrBMI',
                defaultMessage: 'Search flights',
                description: 'Button to search for flights when user can edit the trip',
              })}
            </TextButton>
          )}
          {!canEdit && (
            <TextButton
              kind="secondary"
              to={flightSearchUrl}
              onClick={onFlightSearchClick}
            >
              {intl.formatMessage({
                id: 'ErbOT1f3u3',
                defaultMessage: 'Find flights',
                description: 'Button to find flights when user cannot edit the trip',
              })}
            </TextButton>
          )}
        </Group>
        {showPrice && (
          <Caption colour="neutral-four" variant="medium">
            {intl.formatMessage({
              id: 'YdF89xT4pj',
              defaultMessage: '*Price and availability are subject to change.',
              description: 'Disclaimer about flight prices and availability being subject to change',
            })}
          </Caption>
        )}
      </VerticalSpacer>
    </>
  )
}
